import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Snackbar, IconButton, Alert, CircularProgress
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from 'react-i18next';
import { fetchJson } from "../../utils/fetchJson";

export default function TuitionPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ studentId: "", term: "", amount: "" });
  const [snack, setSnack] = useState("");

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true); setError("");
    try {
      const data = await fetchJson("/tuition", {
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
      });
      setRows(data);
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }

  function openCreate() {
    setCurrent(null);
    setForm({ studentId: "", term: "", amount: "" });
    setDialogOpen(true);
  }
  function openEdit(r) {
    setCurrent(r);
    setForm({ studentId: r.studentId, term: r.term, amount: r.amount });
    setDialogOpen(true);
  }

  async function handleSave() {
    setError("");
    if (!form.studentId || !form.amount) { setError(t('tuition.errors.studentAndAmountRequired')); return; }
    try {
      const url = current ? `/tuition/${current.id}` : `/tuition`;
      const method = current ? "PUT" : "POST";
      await fetchJson(url, {
        method, headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`
        },
        body: JSON.stringify(form)
      });
      setSnack(current ? t('tuition.messages.updated') : t('tuition.messages.created'));
      setDialogOpen(false);
      load();
    } catch (e) { setError(e.message) }
  }

  async function handleDelete(id) {
    if (!window.confirm(t('tuition.confirmDelete'))) return;
    try {
      await fetchJson(`/tuition/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` }
      });
      setSnack(t('tuition.messages.deleted'));
      load();
    } catch (e) { alert(e.message) }
  }

  const columns = [
    { field: "id", headerName: t('tuition.table.headers.id'), width: 70 },
    { field: "studentId", headerName: t('tuition.table.headers.student'), width: 120 },
    { field: "term", headerName: t('tuition.table.headers.term'), width: 100 },
    { field: "amount", headerName: t('tuition.table.headers.amount'), width: 100 },
    {
      field: "actions",
      headerName: t('tuition.table.headers.actions'),
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" onClick={() => openEdit(row)} startIcon={<EditIcon />}>{t('common.edit')}</Button>
          <Button size="small" color="error" onClick={() => handleDelete(row.id)} startIcon={<DeleteIcon />}>{t('common.delete')}</Button>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p:4 }}>
      <Typography variant="h4" gutterBottom>{t('tuition.title')}</Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ mb:2 }}>{t('tuition.actions.newTuition')}</Button>
      {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
      {loading
        ? <Box sx={{ textAlign:"center", mt:4 }}><CircularProgress/></Box>
        : <div style={{ height: 500, width: "100%" }}>
            <DataGrid rows={rows} columns={columns} pageSize={10}/>
          </div>
      }

      <Dialog open={dialogOpen} onClose={()=>setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{current ? t('tuition.dialog.editTitle') : t('tuition.dialog.createTitle')}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
          <TextField
            label={t('tuition.fields.studentId')}
            fullWidth sx={{ mt:2 }}
            value={form.studentId}
            onChange={e=>setForm(f=>({...f,studentId:e.target.value}))}
          />
          <TextField
            label={t('tuition.fields.term')}
            fullWidth sx={{ mt:2 }}
            value={form.term}
            onChange={e=>setForm(f=>({...f,term:e.target.value}))}
          />
          <TextField
            label={t('tuition.fields.amount')}
            fullWidth sx={{ mt:2 }}
            value={form.amount}
            onChange={e=>setForm(f=>({...f,amount:e.target.value}))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave}>{t('common.save')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={()=>setSnack("")}
        message={snack}
        action={
          <IconButton size="small" color="inherit" onClick={()=>setSnack("")}>
            <CloseIcon fontSize="small"/>
          </IconButton>
        }
      />
    </Box>
  );
}
